import React, { useEffect, useState } from 'react'
import { Modal, ModalContent } from 'shared/components/common/modal'
import { t } from 'src/translations'
import { useFormatPriceCallback } from 'src/lib/js/hooks/useFormatPrice'
import { MissingItemInput, RefundableItem } from '../lib/refundItemPricing'
import RecoveryModalHeader from './RecoveryModalHeader'
import RecoveryModalFooter from './RecoveryModalFooter'
import RecoveryChoiceRow from './RecoveryChoiceRow'

interface Props {
  isOpen: boolean
  // The order's refundable items, mapped from the completedOrder query by the card.
  items: RefundableItem[]
  onBack: () => void
  onClose: () => void
  // Fired on Next with the items the guest flagged (backend MissingItemInput shape).
  onSelectItems: (items: MissingItemInput[]) => void
}

// D2 "Which items had an issue?" — checkbox rows, reached when the D1 reason routes to
// itemSelector; Next is disabled until an item is chosen. Prices are in dollars.
export const ItemSelectionModal = ({
  isOpen,
  items,
  onBack,
  onClose,
  onSelectItems
}: Props) => {
  const [selectedGuids, setSelectedGuids] = useState<Set<string>>(new Set())
  // The restaurant's currency and locale, not the browser's — a UK guest must not see dollars.
  const formatPrice = useFormatPriceCallback()

  // Reset selection each time the modal (re)opens so a prior pick never carries over.
  useEffect(() => {
    if (isOpen) setSelectedGuids(new Set())
  }, [isOpen])

  const toggle = (selectionGuid: string) => {
    setSelectedGuids((prev) => {
      const next = new Set(prev)
      if (next.has(selectionGuid)) next.delete(selectionGuid)
      else next.add(selectionGuid)
      return next
    })
  }

  const next = () => {
    if (selectedGuids.size === 0) return
    onSelectItems(
      [...selectedGuids].map((selectionGuid) => ({ selectionGuid }))
    )
  }

  // Enter/Space toggles, as a native checkbox does. No arrow keys: each checkbox is its own tab
  // stop, so there is no roving tabindex to move (that is useRovingRadioGroup's job).
  const toggleOnEnterOrSpace =
    (selectionGuid: string) => (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        toggle(selectionGuid)
      }
    }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      testId='recovery-item-modal'
      className='refundSupportModal'
    >
      <ModalContent
        wrapperClassName='refundSupportWrapper'
        contentClassName='refundResolutionModal'
        ariaLabelledBy='recovery-item-title'
        ariaDescribedBy='recovery-item-subtitle'
      >
        <RecoveryModalHeader onBack={onBack} />
        <div className='refundResolutionBody'>
          <h2 id='recovery-item-title' className='title' tabIndex={-1}>
            {t('order-confirmation.recovery.items.title')}
          </h2>
          <p id='recovery-item-subtitle' className='subtitle'>
            {t('order-confirmation.recovery.items.subtitle')}
          </p>

          {/* Named off the heading, so the group isn't announced as an anonymous one. Each row
              keeps its own tab stop — the expected pattern for checkboxes. */}
          <div
            role='group'
            aria-labelledby='recovery-item-title'
            className='resolutionOptions'
          >
            {items.map((item) => (
              <RecoveryChoiceRow
                key={item.selectionGuid}
                role='checkbox'
                isSelected={selectedGuids.has(item.selectionGuid)}
                className='recoveryItem'
                testId={`recovery-item-${item.selectionGuid}`}
                tabIndex={0}
                onClick={() => toggle(item.selectionGuid)}
                onKeyDown={toggleOnEnterOrSpace(item.selectionGuid)}
              >
                <span className='optionTitle'>{item.name}</span>
                {item.modifiers.length > 0 && (
                  <span className='detail'>{item.modifiers.join(', ')}</span>
                )}
                <span className='detail itemPrice'>
                  {formatPrice(item.price)}
                </span>
              </RecoveryChoiceRow>
            ))}
          </div>

          <p className='recoveryFinePrint'>
            {t('order-confirmation.recovery.items.claims-notice')}
          </p>
        </div>
        <RecoveryModalFooter>
          <button
            type='button'
            className='resolutionCta'
            data-testid='recovery-item-next'
            disabled={selectedGuids.size === 0}
            onClick={next}
          >
            {t('order-confirmation.recovery.next')}
          </button>
        </RecoveryModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default ItemSelectionModal
